import { useSearchParams } from "react-router";
import { Library, Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { AddAssetMenu } from "./add-asset-menu";
import { AssetCard, AssetListRow } from "./asset-card";
import { AssetFolderBreadcrumb } from "./asset-folder-breadcrumb";
import { ContentToolbar } from "./content-toolbar";
import { useContentAssets } from "./use-content-assets";

// Phase 7 sub-bucket 13. Admin content library list page.
//
// URL state:
//   ?folder=<id>         folder scope (omit = root)
//   ?q=<term>            title contains
//   ?type=<asset_type>   chip filter (folder | deck | video_link | …)
//   ?archived=1          flip to archived-only view
//   ?view=grid|list      default grid
//
// Filter state is canonical in the URL so back/forward navigation and
// share links both work. The list query key embeds the filter tuple, so
// flipping any param triggers a fresh fetch — the dataset is small enough
// (~500-row cap) that re-fetching beats in-memory filtering on this page.
export function ContentListPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const folderParam = searchParams.get("folder");
  const q = searchParams.get("q") ?? "";
  const type = searchParams.get("type");
  const archived = searchParams.get("archived") === "1";
  const view: "grid" | "list" =
    searchParams.get("view") === "list" ? "list" : "grid";

  const query = useContentAssets({
    folder: folderParam ?? undefined,
    q,
    type: type ?? undefined,
    archived,
  });

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  const rows = query.data ?? [];
  const filtersActive = Boolean(q.trim()) || Boolean(type) || archived;

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Content library
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Enablement assets — files, embedded videos, slides, meeting links,
            folders. Visibility controls who can see each asset on the partner
            portal.
          </p>
        </div>
        <AddAssetMenu parentAssetId={folderParam} />
      </header>

      <AssetFolderBreadcrumb folderId={folderParam} />

      <ContentToolbar
        q={q}
        type={type}
        archived={archived}
        view={view}
        onChange={setParam}
      />

      <div className="mt-6">
        {query.isLoading ? (
          view === "grid" ? (
            <GridSkeleton />
          ) : (
            <ListSkeleton />
          )
        ) : query.isError ? (
          <EmptyState
            icon={Library}
            title="Couldn't load content"
            description={query.error?.message ?? "Try refreshing."}
          />
        ) : rows.length === 0 ? (
          filtersActive ? (
            <EmptyState
              icon={Search}
              title="No assets match these filters"
              description="Try clearing the search box or relaxing the filters."
            />
          ) : (
            <EmptyState
              icon={Library}
              title={
                folderParam
                  ? "No assets in this folder yet"
                  : "No content assets yet"
              }
              description={
                folderParam
                  ? "Add an asset from the menu above, or move existing assets into this folder."
                  : "Upload your first deck, embed a video, or paste a meeting link."
              }
              action={<AddAssetMenu parentAssetId={folderParam} variant="empty" />}
            />
          )
        ) : view === "grid" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((a) => (
              <AssetCard key={a.id} asset={a} />
            ))}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-2.5">Title</th>
                  <th className="px-4 py-2.5">Type</th>
                  <th className="px-4 py-2.5">Visibility</th>
                  <th className="px-4 py-2.5 text-right">Updated</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {rows.map((a) => (
                  <AssetListRow key={a.id} asset={a} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-44 rounded-xl" />
      ))}
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="rounded-xl border bg-card">
      <div className="space-y-3 p-4">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="ml-auto h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}
